/**
 * Bandeau de notification quand la paroisse active est désactivée par
 * l'administration diocésaine (F2 — multi-tenant V1.5).
 *
 * Récupère le statut de la paroisse courante via
 * GET /api/micro/parishes/:id (Hibernate retourne isActive).
 * Si false → bandeau orange en haut de page avec le message + raison.
 *
 * Pas visible si :
 *  - aucune paroisse active sélectionnée (rien à afficher)
 *  - la paroisse est active (90+ % des cas)
 *  - l'utilisateur est sur /select-parish ou /
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { AlertTriangle } from "lucide-react";

import { useParish } from "@/contexts/parish-context";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/lib/i18n/i18n";

type ParishDetail = {
  id: string;
  name: string;
  isActive: boolean;
  deactivationReason?: string | null;
  deactivatedAt?: string | null;
};

export function ParishStatusBanner() {
  const t = useTranslation();
  const { user } = useAuth();
  const { currentParishId, currentParishName } = useParish();
  const [location] = useLocation();

  // Pas de bannière sur les pages avant sélection de paroisse
  const HIDE_ROUTES = ["/", "/select-parish"];
  const onHiddenRoute = HIDE_ROUTES.some((r) => location === r);

  const { data } = useQuery<ParishDetail>({
    queryKey: [`/api/micro/parishes/${currentParishId}`],
    enabled: !!user && !!currentParishId && !onHiddenRoute,
    // 30 sec : un curé peut perdre l'accès dans la minute si l'admin désactive
    staleTime: 30 * 1000,
  });

  if (!data || data.isActive) return null;

  const name = data.name || currentParishName || "";
  const reason = data.deactivationReason ?? "";

  return (
    <div
      role="alert"
      className="border-l-4 border-orange-500 bg-orange-50 px-4 py-3"
      data-testid="banner-parish-deactivated"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-orange-900">
            {t("common.parishStatus.deactivatedTitle", { name })}
          </div>
          <p className="text-orange-800 mt-1">
            {t("common.parishStatus.deactivatedMessage")}
            {reason && (
              <>
                {" "}
                <span className="text-orange-700 italic">({reason})</span>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
